import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { statsService } from '../services/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 4px 0 0;
`;

const PeriodSwitch = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 3px;
`;

const PeriodBtn = styled.button<{ active: boolean }>`
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.active ? 'white' : 'transparent'};
  color: ${p => p.active ? '#111827' : '#6b7280'};
  box-shadow: ${p => p.active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const Card = styled.div<{ accent?: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  border-left: 4px solid ${p => p.accent || '#3b82f6'};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardIcon = styled.div`font-size: 22px; margin-bottom: 4px;`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
`;

const CardLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const CardHint = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 16px;
  align-items: center;
  padding: 12px 24px;
  border-bottom: 1px solid #f9fafb;
  font-size: 14px;
  color: #1f2937;
  &:last-child { border-bottom: none; }
`;

const HeadRow = styled(Row)`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Num = styled.div`
  text-align: right;
  font-variant-numeric: tabular-nums;
  min-width: 80px;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

interface AnalyticsData {
  days: number;
  menu_views: number;
  qr_scans: number;
  qr_codes: { name: string; scan_count: number }[];
  sessions: number;
  avg_duration: number;
  total_duration: number;
  daily: { day: string; sessions: number; avg_duration: number }[];
}

const PERIODS = [7, 30, 90];

/** 75 → "1m 15s", 3700 → "1h 1m" */
const formatDuration = (sec: number) => {
  if (!sec) return '0s';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return s ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
};

const formatDay = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });

const Analytics: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    statsService.getAnalytics(days)
      .then(res => { if (res.success) setData(res.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  const fmt = (n?: number) => loading ? '—' : (n ?? 0).toLocaleString('it-IT');

  return (
    <Container>
      <Header>
        <div>
          <Title>Analisi</Title>
          <Subtitle>Come i clienti usano il menu digitale</Subtitle>
        </div>
        <PeriodSwitch>
          {PERIODS.map(p => (
            <PeriodBtn key={p} active={days === p} onClick={() => setDays(p)}>{p} giorni</PeriodBtn>
          ))}
        </PeriodSwitch>
      </Header>

      <Grid>
        <Card accent="#3b82f6">
          <CardIcon>👁️</CardIcon>
          <CardValue>{fmt(data?.menu_views)}</CardValue>
          <CardLabel>Visite menu pubblico</CardLabel>
          <CardHint>Totale, 1 per dispositivo al giorno</CardHint>
        </Card>
        <Card accent="#8b5cf6">
          <CardIcon>📊</CardIcon>
          <CardValue>{fmt(data?.qr_scans)}</CardValue>
          <CardLabel>Scansioni QR totali</CardLabel>
          <CardHint>Somma di tutti i QR code</CardHint>
        </Card>
        <Card accent="#10b981">
          <CardIcon>⏱️</CardIcon>
          <CardValue>{loading ? '—' : formatDuration(data?.avg_duration ?? 0)}</CardValue>
          <CardLabel>Tempo medio sul menu</CardLabel>
          <CardHint>Ultimi {days} giorni</CardHint>
        </Card>
        <Card accent="#f59e0b">
          <CardIcon>📱</CardIcon>
          <CardValue>{fmt(data?.sessions)}</CardValue>
          <CardLabel>Sessioni sul menu</CardLabel>
          <CardHint>Ultimi {days} giorni · {loading ? '—' : formatDuration(data?.total_duration ?? 0)} in totale</CardHint>
        </Card>
      </Grid>

      <Section>
        <SectionHeader>📅 Andamento giornaliero</SectionHeader>
        {loading ? (
          <Empty>Caricamento...</Empty>
        ) : !data?.daily.length ? (
          <Empty>Nessuna sessione registrata nel periodo</Empty>
        ) : (
          <>
            <HeadRow><div>Giorno</div><Num>Sessioni</Num><Num>Tempo medio</Num></HeadRow>
            {data.daily.map(d => (
              <Row key={d.day}>
                <div>{formatDay(d.day)}</div>
                <Num>{d.sessions.toLocaleString('it-IT')}</Num>
                <Num>{formatDuration(d.avg_duration)}</Num>
              </Row>
            ))}
          </>
        )}
      </Section>

      <Section>
        <SectionHeader>🔗 Scansioni per QR code</SectionHeader>
        {loading ? (
          <Empty>Caricamento...</Empty>
        ) : !data?.qr_codes.length ? (
          <Empty>Nessun QR code creato</Empty>
        ) : (
          data.qr_codes.map((q, i) => (
            <Row key={i} style={{ gridTemplateColumns: '1fr auto' }}>
              <div>{q.name}</div>
              <Num>{q.scan_count.toLocaleString('it-IT')}</Num>
            </Row>
          ))
        )}
      </Section>
    </Container>
  );
};

export default Analytics;
